import * as THREE from 'three';
import {
  PALETTE,
  placePose,
  roundPose,
  type FurnitureWire,
  type MovesWire,
  type PieceInfo,
  type PiecePose,
  type PlacementSpace,
} from '@vrlearning/lesson-schema';
import {
  HoldSender,
  PaletteFlick,
  PoseBuffer,
  TURN,
  carryTarget,
  keyAction,
  normaliseTurn,
  refusalText,
  settleTurn,
  teleportCarry,
  turnBy,
  type GrabAnswer,
  type RigPlace,
} from '../../engine/furnishing.js';
import type { FurnishingLink } from '../../engine/furnishing-link.js';
import { REACH_METRES } from '../../engine/placement.js';
import type { FurnishAction } from './furnish-lectern.js';
import type { FurnitureView, PieceLook } from './pieces.js';

/**
 * The furnish step (ADR-037; spec "Grabbing, turning, colouring"): what a pupil's presses, rays,
 * thumbstick and keys do to the pieces, and what the furniture messages do to what they see.
 *
 * - A press on a free piece within 8 m asks the server for it (the piece pulses) and holds it once
 *   granted; refused or unanswered in 2 s, a plate says why for 1.5 s. The press is claimed either
 *   way, so it is never also a select.
 * - Held, the piece stands where the ray meets the floor, 0.8–6 m from the eyes, on the walkable
 *   floor; the holding thumbstick turns it at 90° a second and it settles on 15° when let go; ↺ ↻,
 *   Q and E turn it by 15°; the palette, a flick of the thumbstick or 1–8 colour it. Holds go to the
 *   server by the send rule; the release drops it where it stands.
 * - Other pupils' pieces are drawn 120 ms behind their streamed poses, amber while taken; a hold
 *   that ends eases them to where the server has them. A furniture message that no longer names
 *   this headset as the holder ends its hold at once.
 * - A room button fades to that room's viewpoint; a held piece keeps its place beside the pupil.
 */

type Vec3 = readonly [number, number, number];

/** What the interaction needs of the side lectern (furnish-lectern.ts). */
export type LecternControls = {
  holding: boolean;
  locked: boolean;
  material: PiecePose['material'] | undefined;
};

export type InteractionDeps = {
  readonly link: FurnishingLink;
  readonly view: Pick<
    FurnitureView,
    | 'add'
    | 'remove'
    | 'has'
    | 'ids'
    | 'pose'
    | 'look'
    | 'setPose'
    | 'setLook'
    | 'setPointed'
    | 'along'
  >;
  readonly lectern?: LecternControls;
  /** The set's pieces and their signed URLs. */
  readonly pieces: readonly PieceInfo[];
  readonly media: Readonly<Record<string, string>>;
  /** Milliseconds, for the send rule and the interpolation. */
  readonly now: () => number;
  /** Where the rig stands on the floor, and its turn. */
  readonly rig: () => RigPlace;
  /** Where the pupil's eyes are, in the world. */
  readonly eye: () => Vec3;
  /** A fade teleport: `arrive` at the black. */
  readonly travel: (arrive: () => void) => void;
  /** Whether a fade is running: no holds are sent then. */
  readonly travelling: () => boolean;
  /** Where pupils' work may stand in this world: the villa's plan, or a package's plaatsruimte. */
  readonly space: PlacementSpace;
  /** Stands the rig at a room's viewpoint (at the black). */
  readonly placeRoom: (room: string) => void;
  readonly redrawShadow: () => void;
  /** A plate over a piece for 1.5 s: why a grab was refused, or that the villa is locked. */
  readonly notice: (text: string, pieceId: string) => void;
  /** Whether this is the furnish step: grabbing is off in the others. */
  readonly active: () => boolean;
  /** How far along a ray the lectern's own buttons are, if it lands on them: they win over a piece. */
  readonly uiAlong?: (ray: THREE.Ray) => number | undefined;
};

type Held = { id: string; pose: PiecePose; turning: boolean };

export class FurnishInteraction {
  readonly #deps: InteractionDeps;
  readonly #infos: Map<string, PieceInfo>;
  readonly #buffers = new Map<string, PoseBuffer>();
  readonly #sender = new HoldSender();
  readonly #flick = new PaletteFlick();
  #wire: FurnitureWire | undefined;
  #held: Held | undefined;
  #pending: { id: string; released: boolean } | undefined;
  #disposed = false;

  constructor(deps: InteractionDeps) {
    this.#deps = deps;
    this.#infos = new Map(deps.pieces.map((piece) => [piece.id, piece]));
    deps.link.onFurniture((wire) => this.#furniture(wire));
    deps.link.onMoves((moves) => this.#moves(moves));
  }

  /** The piece this pupil holds, if any. */
  get holding(): string | undefined {
    return this.#held?.id;
  }

  get locked(): boolean {
    return this.#wire?.locked === true;
  }

  /** A press: a free piece along the ray is asked for. True when the press is the furniture's. */
  claim(ray: THREE.Ray): boolean {
    if (this.#disposed || !this.#deps.active() || this.#held || this.#pending) return false;
    const hit = this.#deps.view.along(ray, REACH_METRES);
    if (!hit) return false;
    const ui = this.#deps.uiAlong?.(ray);
    if (ui !== undefined && ui <= hit.distance) return false;
    const { id } = hit;
    if (this.locked) {
      this.#deps.notice(refusalText('villa-locked', this.#deps.space), id);
      return true;
    }
    // Bigger than the space allows: drawn where it starts, and not to be taken (the hub says the same).
    const size = this.#infos.get(id)?.size;
    if (size && Math.max(...size) > this.#deps.space.maxSize + 1e-9) {
      this.#deps.notice(refusalText('too-big', this.#deps.space), id);
      return true;
    }
    if (this.#deps.view.look(id) === 'taken') {
      this.#deps.notice(refusalText('piece-taken'), id);
      return true;
    }
    const pending = { id, released: false };
    this.#pending = pending;
    this.#deps.view.setLook(id, 'pending');
    void this.#deps.link.grab(id).then((answer) => this.#answered(pending, answer));
    return true;
  }

  /** The holding ray, each frame (controller) or each mouse move: where the piece stands. */
  move(ray: THREE.Ray): void {
    const held = this.#held;
    if (!held) return;
    const size = this.#infos.get(held.id)?.size ?? [0.5, 0.5, 0.5];
    // The first the ray meets of a table, a wall or the floor; a piece hung on a wall faces out of it.
    const target = carryTarget(
      {
        origin: [ray.origin.x, ray.origin.y, ray.origin.z],
        direction: [ray.direction.x, ray.direction.y, ray.direction.z],
      },
      this.#deps.eye(),
      this.#deps.space,
      size,
    );
    const { y: _y, ...floor } = held.pose;
    this.#set({
      ...floor,
      at: target.at,
      ...(target.y > 0 ? { y: target.y } : {}),
      ...(target.turn !== undefined ? { turn: target.turn } : {}),
    });
  }

  /** The press let go: the held piece stands where it is. */
  end(): void {
    if (this.#pending) this.#pending.released = true;
    const held = this.#held;
    if (!held) return;
    const pose = this.#rounded(held.id, held.pose);
    this.#held = undefined;
    this.#deps.link.drop({ pieceId: held.id, ...pose });
    this.#deps.view.setPose(held.id, pose);
    this.#deps.view.setLook(held.id, 'free');
    this.#holdingChanged();
    this.#deps.redrawShadow();
  }

  /**
   * Once a frame: the holding thumbstick (x turns, y flicks the palette), the hold's send rule, and
   * other pupils' pieces drawn from their poses.
   */
  frame(dt: number, thumbstick: readonly [number, number] = [0, 0]): void {
    if (this.#disposed) return;
    const held = this.#held;
    if (held) {
      const [x, y] = thumbstick;
      if (Math.abs(x) > TURN.dead) {
        held.turning = true;
        this.#set({ ...held.pose, turn: turnBy(held.pose.turn, x, dt) });
      } else if (held.turning) {
        held.turning = false;
        this.#set({ ...held.pose, turn: settleTurn(held.pose.turn) });
      }
      const step = this.#flick.update(y);
      if (step !== 0) this.#paint(step);
      const now = this.#deps.now();
      if (!this.#deps.travelling() && this.#sender.due(held.pose, now)) {
        this.#deps.link.hold({ pieceId: held.id, ...this.#rounded(held.id, held.pose) });
        this.#sender.sent(held.pose, now);
      }
    }
    const now = this.#deps.now();
    for (const [id, buffer] of this.#buffers) {
      if (id === held?.id) continue;
      const pose = buffer.sample(now);
      if (pose) this.#deps.view.setPose(id, pose);
    }
  }

  /** A button of the side lectern: a room, a colour, or a turn. */
  press(action: FurnishAction): void {
    if ('room' in action) {
      this.#goTo(action.room);
      return;
    }
    const held = this.#held;
    if (!held) return;
    if ('material' in action) this.#set({ ...held.pose, material: action.material });
    else this.#set({ ...held.pose, turn: settleTurn(normaliseTurn(held.pose.turn + action.turn)) });
  }

  /** The desktop preview's keys while holding: Q and E turn by 15°, 1–8 colour. True when used. */
  key(key: string): boolean {
    if (!this.#held) return false;
    const action = keyAction(key);
    if (!action) return false;
    this.press(action);
    return true;
  }

  /** The piece the rays point at (the first that finds one), lit faintly while free. */
  hover(rays: readonly THREE.Ray[]): number | undefined {
    if (!this.#deps.active()) {
      this.#deps.view.setPointed(undefined);
      return undefined;
    }
    for (const ray of rays) {
      const hit = this.#deps.view.along(ray, REACH_METRES);
      if (!hit) continue;
      const ui = this.#deps.uiAlong?.(ray);
      if (ui !== undefined && ui <= hit.distance) continue;
      this.#deps.view.setPointed(hit.id);
      return hit.distance;
    }
    this.#deps.view.setPointed(undefined);
    return undefined;
  }

  /** The step changed: leaving the furnish step lets go of what is held, where it stands. */
  stepChanged(): void {
    if (!this.#deps.active()) this.end();
  }

  /** A room's viewpoint by fade; a held piece keeps its place beside the pupil. */
  #goTo(room: string): void {
    if (!this.#deps.space.rooms.some((candidate) => candidate.id === room)) return;
    this.#deps.travel(() => {
      const from = this.#deps.rig();
      this.#deps.placeRoom(room);
      const to = this.#deps.rig();
      const held = this.#held;
      if (held) {
        const size = this.#infos.get(held.id)?.size;
        // A floor point: a piece that hung or stood raised lands on the floor.
        const { y: _y, ...floor } = held.pose;
        this.#set({
          ...floor,
          at: teleportCarry(held.pose.at, from, to, this.#deps.space, size),
        });
      }
      this.#deps.redrawShadow();
    });
  }

  /**
   * The pose as it goes to the hub, to the centimetre. A raised piece rounded across a table's edge
   * would be put on the floor by the hub: of the four roundings of its place, the first the space still
   * holds at that height is the one sent.
   */
  #rounded(id: string, pose: PiecePose): PiecePose {
    const rounded = roundPose(pose);
    if (!pose.y || pose.y <= 0.01) return rounded;
    const size = this.#infos.get(id)?.size ?? [0.5, 0.5, 0.5];
    const space = this.#deps.space;
    const holds = (at: readonly [number, number]): boolean =>
      Math.abs(placePose(space, { at, y: rounded.y ?? 0 }, size).y - (rounded.y ?? 0)) < 0.005;
    const cm = (v: number): number => Math.round(v * 100) / 100;
    if (holds(rounded.at)) return rounded;
    for (const dx of [0, 0.01, -0.01])
      for (const dz of [0, 0.01, -0.01]) {
        const at: [number, number] = [cm(rounded.at[0] + dx), cm(rounded.at[1] + dz)];
        if (holds(at)) return { ...rounded, at };
      }
    return rounded;
  }

  #paint(step: -1 | 1): void {
    const held = this.#held;
    if (!held) return;
    const at = PALETTE.indexOf(held.pose.material as (typeof PALETTE)[number]);
    const next = at < 0 ? (step > 0 ? 0 : PALETTE.length - 1) : at + step;
    const material = PALETTE[(next + PALETTE.length) % PALETTE.length]!;
    this.#set({ ...held.pose, material });
  }

  #set(pose: PiecePose): void {
    const held = this.#held;
    if (!held) return;
    held.pose = pose;
    this.#deps.view.setPose(held.id, pose);
    if (this.#deps.lectern) this.#deps.lectern.material = pose.material;
  }

  #answered(pending: { id: string; released: boolean }, answer: GrabAnswer): void {
    if (this.#pending === pending) this.#pending = undefined;
    if (this.#disposed) return;
    const { id } = pending;
    if (answer !== 'granted') {
      this.#deps.view.setLook(id, this.#lookFromWire(id));
      this.#deps.notice(refusalText(answer, this.#deps.space), id);
      return;
    }
    const pose = this.#deps.view.pose(id);
    if (!pose || !this.#deps.active()) {
      // Granted as the step moved on, or the piece went: let it go where it stands.
      if (pose) this.#deps.link.drop({ pieceId: id, ...this.#rounded(id, pose) });
      this.#deps.view.setLook(id, 'free');
      return;
    }
    this.#buffers.get(id)?.clear();
    this.#buffers.delete(id);
    this.#held = { id, pose, turning: false };
    this.#sender.reset();
    this.#deps.view.setLook(id, 'mine');
    // It casts no shadow while held: the one where it stood goes with the next shadow pass.
    this.#deps.redrawShadow();
    this.#holdingChanged();
    // Let go before the answer came: it stays where it stood.
    if (pending.released) this.end();
  }

  #holdingChanged(): void {
    const lectern = this.#deps.lectern;
    if (!lectern) return;
    lectern.holding = this.#held !== undefined;
    lectern.material = this.#held?.pose.material;
  }

  #lookFromWire(id: string): PieceLook {
    const holder = this.#wire?.pieces.find((piece) => piece.id === id)?.heldBy ?? null;
    return holder !== null && holder !== this.#deps.link.self ? 'taken' : 'free';
  }

  #buffer(id: string): PoseBuffer {
    let buffer = this.#buffers.get(id);
    if (!buffer) {
      buffer = new PoseBuffer();
      const pose = this.#deps.view.pose(id);
      if (pose) buffer.push(pose, this.#deps.now());
      this.#buffers.set(id, buffer);
    }
    return buffer;
  }

  #furniture(wire: FurnitureWire): void {
    if (this.#disposed) return;
    this.#wire = wire;
    const view = this.#deps.view;
    const self = this.#deps.link.self;
    const now = this.#deps.now();
    let shadow = false;
    const named = new Set(wire.pieces.map((piece) => piece.id));
    // A piece the school deleted leaves the villa, and a pupil holding it holds nothing.
    for (const id of view.ids) {
      if (named.has(id)) continue;
      if (this.#held?.id === id) {
        this.#held = undefined;
        this.#holdingChanged();
      }
      view.remove(id);
      this.#buffers.delete(id);
      shadow = true;
    }
    for (const piece of wire.pieces) {
      const pose: PiecePose = {
        at: piece.at,
        turn: piece.turn,
        material: piece.material,
        ...(piece.y ? { y: piece.y } : {}),
      };
      if (!view.has(piece.id)) {
        const info = this.#infos.get(piece.id);
        if (!info) continue;
        view.add(info, pose, this.#deps.media[info.media]);
        shadow = true;
      }
      const mine = self !== undefined && piece.heldBy === self;
      if (this.#held?.id === piece.id) {
        if (mine && !wire.locked) continue;
        // The hold ended on the server (locked, paused, idle, a reconnect): the piece stays where
        // the server has it.
        this.#held = undefined;
        this.#holdingChanged();
        view.setPose(piece.id, pose);
        view.setLook(piece.id, 'free');
        if (wire.locked) this.#deps.notice(refusalText('villa-locked', this.#deps.space), piece.id);
        shadow = true;
        continue;
      }
      if (this.#pending?.id === piece.id) continue;
      if (piece.heldBy !== null && !mine) {
        if (view.look(piece.id) !== 'taken') {
          view.setLook(piece.id, 'taken');
          this.#buffer(piece.id).push(pose, now);
          // Carried now: its shadow where it stood goes.
          shadow = true;
        }
        continue;
      }
      if (mine) {
        // Held for this headset by a hold it does not know of (a reconnect's race): let it go.
        this.#deps.link.drop({ pieceId: piece.id, ...pose });
      }
      const was = view.look(piece.id);
      if (was === 'taken') {
        this.#buffer(piece.id).settle(pose, now);
      } else {
        const before = view.pose(piece.id);
        if (
          !before ||
          before.at[0] !== pose.at[0] ||
          before.at[1] !== pose.at[1] ||
          before.turn !== pose.turn ||
          (before.y ?? 0) !== (pose.y ?? 0) ||
          before.material !== pose.material
        ) {
          this.#buffers.delete(piece.id);
          view.setPose(piece.id, pose);
          shadow = true;
        }
      }
      if (was !== 'free') {
        view.setLook(piece.id, 'free');
        // Put down: it casts again, where it stands.
        shadow = true;
      }
    }
    if (this.#deps.lectern) this.#deps.lectern.locked = wire.locked;
    if (shadow) this.#deps.redrawShadow();
  }

  #moves(moves: MovesWire): void {
    if (this.#disposed) return;
    const now = this.#deps.now();
    for (const move of moves.pieces) {
      if (move.id === this.#held?.id || !this.#deps.view.has(move.id)) continue;
      if (this.#deps.view.look(move.id) !== 'taken') this.#deps.view.setLook(move.id, 'taken');
      this.#buffer(move.id).push(
        {
          at: move.at,
          turn: move.turn,
          material: move.material,
          ...(move.y ? { y: move.y } : {}),
        },
        now,
      );
    }
  }

  dispose(): void {
    this.#disposed = true;
    this.#buffers.clear();
    this.#deps.link.dispose();
  }
}
